import { createRouter, createMemoryHistory, createWebHistory } from 'vue-router'
import routes from './routes'
import { auth } from '../services/firebase/config'
import { useUserStore } from '../stores/userStore'
import { safeReplaceState } from '@/utils/historyState'

// Track initial auth state loading
let authInitialized = false
let initialAuthStatePromise = new Promise((resolve) => {
  const unsubscribe = auth.onAuthStateChanged(() => {
    authInitialized = true
    unsubscribe()
    resolve()
  })
})

export default function (/* { store, ssrContext } */) {
  const createHistory = import.meta.env.SSR ? createMemoryHistory : createWebHistory

  // Always use root path for both development and production
  const routerBase = '/'

  const Router = createRouter({
    // Preserve scroll on history back/forward for UI backstack and restore saved positions
    scrollBehavior: (to, from, savedPosition) => {
      // If browser provides a saved position (back/forward), use it
      if (savedPosition) return savedPosition
      // If this popstate came from a UI backstack entry, do not change scroll
      try {
        if (typeof window !== 'undefined') {
          const st = window.history && window.history.state
          if (st && st.uiBackstack) return false
        }
      } catch {}
      // For hash anchors, let browser handle default behavior
      if (to.hash) return undefined
      // Default behavior: keep current position on same route (path/params), else scroll to top
      const toParams = to.params || {}
      const fromParams = from.params || {}
      const toKeys = Object.keys(toParams)
      const fromKeys = Object.keys(fromParams)
      const sameParams =
        toKeys.length === fromKeys.length &&
        toKeys.every((key) => String(toParams[key]) === String(fromParams[key]))
      if (to.path === from.path && sameParams) return false
      return { left: 0, top: 0 }
    },
    routes,
    history: createHistory(routerBase)
  })

  const invalidSegmentRe = /(^|\/)(undefined|null)(\/|$|\?|#)/i
  const isBadString = (value) => {
    if (typeof value !== 'string') return true
    const trimmed = value.trim()
    if (!trimmed) return true
    if (invalidSegmentRe.test(trimmed)) return true
    if (trimmed.startsWith('undefined') || trimmed.startsWith('null')) return true
    return false
  }
  const isBadParamValue = (value) => {
    if (value == null) return true
    if (Array.isArray(value)) return value.some((v) => isBadParamValue(v))
    if (typeof value === 'string') return isBadString(value)
    return false
  }
  const isInvalidLocation = (to) => {
    if (to == null) return true
    if (typeof to === 'string') return isBadString(to)
    if (typeof to !== 'object') return true

    const hasPath = Object.prototype.hasOwnProperty.call(to, 'path')
    const hasName = Object.prototype.hasOwnProperty.call(to, 'name')
    const hasOther = to.query != null || to.params != null || to.hash != null

    if (hasPath) {
      if (to.path == null) return true
      if (typeof to.path !== 'string') return true
      if (isBadString(to.path)) return true
    }

    if (hasName) {
      if (to.name == null) return true
      if (typeof to.name !== 'string') return true
      if (!String(to.name).trim()) return true
    }

    if (to.params != null) {
      if (typeof to.params !== 'object') return true
      for (const val of Object.values(to.params)) {
        if (isBadParamValue(val)) return true
      }
    }

    if (!hasPath && !hasName && !hasOther) return true
    return false
  }
  const wrapNavigate =
    (fn, label) =>
    (to, ...args) => {
      if (isInvalidLocation(to)) {
        if (import.meta.env?.DEV) {
          console.warn(`[router] ignoring ${label}() with invalid location`, to)
        }
        return Promise.resolve(false)
      }
      try {
        const resolved = Router.resolve(to)
        if (resolved && typeof resolved.path === 'string' && isBadString(resolved.path)) {
          if (import.meta.env?.DEV) {
            console.warn(`[router] ignoring ${label}() with invalid resolved path`, resolved)
          }
          return Promise.resolve(false)
        }
      } catch (err) {
        if (import.meta.env?.DEV) {
          console.warn(`[router] ignoring ${label}() due to resolve error`, err)
        }
        return Promise.resolve(false)
      }
      return fn(to, ...args)
    }
  Router.push = wrapNavigate(Router.push.bind(Router), 'push')
  Router.replace = wrapNavigate(Router.replace.bind(Router), 'replace')

  // Navigation Guards
  Router.beforeEach(async (to, from, next) => {
    // Wait for initial auth state to be determined
    if (!authInitialized) {
      await initialAuthStatePromise
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin)
    const requiresStaff = to.matched.some((record) => record.meta.requiresStaff)
    const isLoginPage = to.name === 'login'
    const isRegisterPage = to.name === 'register'

    const currentUser = auth.currentUser
    const isAnonymous = !!(currentUser && currentUser.isAnonymous)
    const isAuthenticated = !!(currentUser && !isAnonymous)
    const userStore = useUserStore()

    // If user is already logged in and tries to access login/register pages
    // Allow anonymous users to access login/register
    if ((isLoginPage || isRegisterPage) && isAuthenticated) {
      return next({ path: '/dashboard' })
    }

    // If route requires auth and user is not logged in
    if (requiresAuth && !isAuthenticated) {
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }

    // If route requires admin role
    if (requiresAdmin || requiresStaff) {
      // Ensure profile is loaded
      if (!userStore.profile) {
        try {
          await userStore.fetchUserProfile()
        } catch (error) {
          console.error('Error fetching user profile:', error)
          return next({ path: '/dashboard' })
        }
      }

      // Check if user has required role
      const roles = userStore.profile?.roles || {}
      const ok = requiresAdmin ? !!roles.admin : !!(roles.admin || roles.moderator)
      if (!ok) {
        return next({ path: '/dashboard' }) // Redirect non-admins
      }
    }

    // Otherwise proceed
    next()
  })

  // Add a global afterEach guard to clear hash if not present in the new route
  if (typeof window !== 'undefined') {
    Router.afterEach((to) => {
      if (!to.hash && window.location.hash) {
        // Remove hash from URL without reloading the page
        safeReplaceState({}, window.location.pathname + window.location.search)
      }
    })
  }

  return Router
}
